import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateDepollutionRequest,
  ICreateUpdateDepollutionItemRequest,
  ICreateUpdateDepollutionStepRequest,
  IDepollutionByVehicleResponse,
  IDepollutionItemsResponse,
  IDepollutionStepsResponse,
  ISingleDepollutionStepResponse,
  ISingleDepollutionResponse,
} from "../models/Depollution";

export default function useDepollution(depollutionId?: string, vehicleId?: string, includeAllSteps = false) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const depollution = useQuery({
    queryKey: ["depollution", depollutionId],
    queryFn: () => get<ISingleDepollutionResponse>(`/depollution/${depollutionId}`),
    enabled: !!depollutionId,
    staleTime: 1000 * 60 * 2,
  });

  const depollutionByVehicle = useQuery({
    queryKey: ["depollution-vehicle", vehicleId],
    queryFn: () => get<IDepollutionByVehicleResponse>(`/depollution/vehicle/${vehicleId}`),
    enabled: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const depollutionItems = useQuery({
    queryKey: ["depollution-items", depollutionId],
    queryFn: () => get<IDepollutionItemsResponse>(`/depollution/${depollutionId}/items`),
    enabled: !!depollutionId,
    staleTime: 1000 * 60,
  });

  const depollutionSteps = useQuery({
    queryKey: ["depollution-steps"],
    queryFn: () => get<IDepollutionStepsResponse>(`/depollution/steps?activeOnly=true`),
    staleTime: 1000 * 60 * 10,
  });

  const allDepollutionSteps = useQuery({
    queryKey: ["depollution-steps-all"],
    queryFn: () => get<IDepollutionStepsResponse>(`/depollution/steps`),
    enabled: includeAllSteps,
    staleTime: 1000 * 60 * 10,
  });

  const createDepollution = useMutation(
    (body: ICreateDepollutionRequest) => post<ICreateDepollutionRequest, ISingleDepollutionResponse>("/depollution", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution", depollutionId]);
        queryClient.invalidateQueries(["depollution-vehicle", vehicleId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["pagedTable", "/depollution/filtered"]);
      },
    }
  );

  const saveDepollutionItem = useMutation(
    (body: ICreateUpdateDepollutionItemRequest) => post("/depollution/items", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution", depollutionId]);
        queryClient.invalidateQueries(["depollution-items", depollutionId]);
        queryClient.invalidateQueries(["depollution-vehicle"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["pagedTable", "/depollution/filtered"]);
      },
    }
  );

  const createDepollutionStep = useMutation(
    (body: ICreateUpdateDepollutionStepRequest) => post<ICreateUpdateDepollutionStepRequest, ISingleDepollutionStepResponse>("/depollution/steps", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution-steps"]);
        queryClient.invalidateQueries(["depollution-steps-all"]);
      },
    }
  );

  const updateDepollutionStep = useMutation(
    (body: ICreateUpdateDepollutionStepRequest) => put<ICreateUpdateDepollutionStepRequest, ISingleDepollutionStepResponse>(`/depollution/steps/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution-steps"]);
        queryClient.invalidateQueries(["depollution-steps-all"]);
      },
    }
  );

  const updateDepollutionStepOrder = useMutation(
    ({ depollutionStepId, newIndex }: { depollutionStepId: string, newIndex: number }) => put(`/depollution/steps/${depollutionStepId}/order?newIndex=${newIndex}`, undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution-steps"]);
        queryClient.invalidateQueries(["depollution-steps-all"]);
      },
    }
  );

  const deleteDepollutionStep = useMutation(
    (depollutionStepId: string) => del(`/depollution/steps/${depollutionStepId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["depollution-steps"]);
        queryClient.invalidateQueries(["depollution-steps-all"]);
      },
    }
  );

  return {
    depollution,
    depollutionByVehicle,
    depollutionItems,
    depollutionSteps,
    allDepollutionSteps,
    createDepollution,
    saveDepollutionItem,
    createDepollutionStep,
    updateDepollutionStep,
    updateDepollutionStepOrder,
    deleteDepollutionStep,
  };
}
